import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import Figure from "../Dashboard/Figure";
import { aiCredentials, aiProjects, aiSkills, aiWorkshop } from "./aiItems";
import type { NonCredlyCert } from "../../../utils/nonCredlyCerts";
import type { Project } from "../../../types/content";

/**
 * ============================================================================
 * AI & Innovation — the Home strip (D-095).
 * ============================================================================
 *
 * `/ai-innovation` shipped in D-094 reachable only from a navbar dropdown, and
 * TODO recorded that as the obvious next thing. This is the strip.
 *
 * EVERY STRING IS THE LAYER'S AND EVERY NUMBER IS A REGISTERED FIGURE. The
 * credential titles, the build names and the skills are read through
 * `aiItems.ts` — the same selection the full route uses, so the two cannot
 * disagree about what counts as AI work — and the three counts go through
 * `<Figure>`, which throws on an id it cannot source and therefore fails the
 * build. Nothing here is authored about the career; the strip's own prose
 * describes the strip.
 *
 * IT IS A SAMPLE, NOT A SECOND HUB. The full route carries twelve items with
 * their descriptions, tags and links to where each is documented. Reproducing
 * that on Home would be a second copy of a consolidation whose entire value is
 * being one place — so this shows the three counts, the newest credentials by
 * name, the builds by name, and the session, and sends the reader on.
 */
/**
 * The reveal the other four card strips on this page use, in ONE place rather
 * than five copies of the same four props (D-061 — material has one home).
 *
 * `whileInView` IS A GESTURE, NOT A STATE (D-089), and knowing the difference
 * is why this is safe here and was not safe on the certifications grid: framer
 * does not propagate a gesture to a child that mounts after the parent has
 * settled, so a keyed list React REUSES can hand an incoming card an observer
 * that has already fired and disconnected — which paints nothing at
 * `opacity: 0` while every markup check passes. This strip is not filtered and
 * its keys never change, so no card is ever swapped into a settled slot. The
 * gate measures PAINT rather than presence anyway, because that reasoning is
 * exactly the kind that stops being true quietly.
 *
 * `initial: false` under `prefers-reduced-motion`, not a shorter duration: a
 * reader who asked for no motion gets the card in its final position rather
 * than a fast version of the animation.
 */
const reveal = (index: number, reduceMotion: boolean | null) => ({
	initial: reduceMotion ? false : { opacity: 0, y: 24 },
	whileInView: { opacity: 1, y: 0 },
	viewport: { once: true, amount: 0.2 },
	transition: { duration: 0.5, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }
});

/**
 * A count card, and since D-100 a LINK to the panel of `/ai-innovation` that
 * holds the detail behind the count — owner instruction, and the same call
 * `DashboardTeaser` made in D-097.
 *
 * The whole card is the link rather than a "view more" inside it: there is no
 * other interactive content here, so a wrapping `<Link>` gives exactly one tab
 * stop and one entry in the accessibility tree. The stretched-`::after` pattern
 * is for cards that carry links of their own; this one does not.
 *
 * `to` IS REQUIRED. A count card whose destination could be omitted is a card
 * that silently stops being clickable when someone adds the fourth one, and
 * TypeScript refusing to compile is a better gate than a sweep noticing later.
 *
 * The accessible name is built from the label, because "6" as link text says
 * nothing about where it goes.
 */
const TeaserCount = ({
	id,
	label,
	to,
	index
}: {
	id: string;
	label: string;
	to: string;
	index: number;
}): JSX.Element => {
	const reduceMotion = useReducedMotion();

	return (
		<motion.div className="card-shell" {...reveal(index, reduceMotion)}>
			<Link
				to={to}
				aria-label={`${label} — open the detail`}
				className="card-face flex h-full flex-col p-5 no-underline focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
			>
				<Figure id={id} className="text-3xl font-semibold leading-none text-[var(--text-strong)]" />
				<span className="mt-2 text-sm font-medium text-[var(--text-secondary)]">{label}</span>
			</Link>
		</motion.div>
	);
};

/** How many credential names the strip sets before it stops listing. */
const NAMED_CREDENTIALS = 3;

export default function AiInnovationTeaser(): JSX.Element | null {
	/* BEFORE the guard below, not after it: a hook that runs only on some
	   renders is a hook-order violation, and the early return is conditional. */
	const reduceMotion = useReducedMotion();

	/* Article II.4 — a section with nothing in it must not ship. `aiItems`
	   already throws when its named items stop resolving, so this guards the
	   derived halves. */
	if (!aiCredentials.length && !aiProjects.length) return null;

	return (
		<section className="section-shell" id="ai-innovation" aria-labelledby="ai-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Consolidated View</p>

				<h2 id="ai-teaser-heading" className="heading">
					<span className="bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] bg-clip-text text-transparent">
						AI
					</span>{" "}
					&amp; Innovation
				</h2>

				<p className="section-standfirst">
					The AI work recorded across this site — credentials, a framework built into delivery, platforms
					shipped and a session run for the organisation — gathered into one view.
				</p>

				<div className="mt-10 grid auto-rows-fr grid-cols-1 gap-5 sm:grid-cols-3">
					{/* Each destination is the panel that OWNS the count, so the
					    number a reader clicks is the number they land among. */}
					<TeaserCount
						index={0}
						id="ai:credentials"
						label="AI credentials"
						to="/ai-innovation#ai-credentials"
					/>
					<TeaserCount index={1} id="ai:builds" label="AI platforms built" to="/ai-innovation#ai-built" />
					<TeaserCount
						index={2}
						id="ai:skills"
						label="Skills in the discipline"
						to="/ai-innovation#ai-capability"
					/>
				</div>

				<div className="mt-6 grid auto-rows-fr grid-cols-1 gap-5 lg:grid-cols-2">
					{/* THE TWO PANELS ARE LINKS TOO, and they take the stretched
					    `::after` rather than a wrapping anchor, because a `<Link>`
					    around this card would contain the chip list and the build
					    list — content a reader can select but not activate, which is
					    fine, and a nested anchor the day one of those gains a link of
					    its own, which is not. The heading carries the link; the
					    pseudo-element carries the hit area. */}
					<motion.div className="card-shell group relative" {...reveal(0, reduceMotion)}>
						<div className="card-face flex h-full flex-col p-6">
							<h3 className="text-base font-semibold text-[var(--text-strong)]">
								<Link
									to="/ai-innovation#ai-capability"
									className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
								>
									{aiSkills.name}
								</Link>
							</h3>
							<ul className="mt-3 flex flex-wrap gap-2">
								{aiSkills.skills.map((skill: string) => (
									<li
										key={skill}
										className="pill-tint border-[var(--cat-violet-border)] bg-[var(--cat-violet-fill)] text-[var(--cat-violet-text)]"
									>
										{skill}
									</li>
								))}
							</ul>
						</div>
					</motion.div>

					<motion.div className="card-shell group relative" {...reveal(1, reduceMotion)}>
						<div className="card-face flex h-full flex-col p-6">
							<h3 className="text-base font-semibold text-[var(--text-strong)]">
								<Link
									to="/ai-innovation#ai-built"
									className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
								>
									Built and taught
								</Link>
							</h3>
							<ul className="mt-3 space-y-2">
								{aiProjects.map((project: Project) => (
									<li
										key={project.id}
										className="text-sm leading-relaxed text-[var(--text-secondary)]"
									>
										{project.name}
									</li>
								))}
								<li className="text-sm leading-relaxed text-[var(--text-secondary)]">
									{aiWorkshop.title}
								</li>
							</ul>
							<p className="mt-4 text-xs leading-relaxed text-[var(--text-muted)]">
								{aiCredentials
									.slice(0, NAMED_CREDENTIALS)
									.map((cert: NonCredlyCert) => cert.title)
									.join(" · ")}
							</p>
						</div>
					</motion.div>
				</div>

				<div className="mt-8">
					<Link
						to="/ai-innovation"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						See every item and where it is documented &rarr;
					</Link>
				</div>
			</div>
		</section>
	);
}
